// Imports for external libraries go here.
import { EditableComponent } from '@adobe/aem-react-editable-components';
import React, { useId, useState, FC, ChangeEvent, useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { Heading, Text, Types, CheckBox, Switch, Button } from '@marriott/mi-ui-library';

// Imports for internal files go here.
import { useCreateSurveyStore } from '../../store/createSurveyStore';
import { DiningProps, amenitiesDetailsProps } from './DiningDetail.type';
import { SurveyWrapper } from '../../molecules/SurveyWrapper';
import { StyledDiningDetail } from './DiningDetails.Styles';
import { TextArea } from '../../atoms/TextArea';
import { CarouselContainer } from '../../molecules/CarouselContainer';
import { brandColorSchemeGenerator, dietryCheckboxData, diningCheckboxData } from '../../util/constants';
import dayjs from 'dayjs';
import DOMPurify from 'isomorphic-dompurify';
import moment from 'moment';
import { updateDatalayer } from '../../util/dataLayer/datalayerUtil';
import { SurveyWrapperInner } from '../../molecules/SurveyWrapperInner';
import { removeXSSScripts } from '../../util/helpers';
import { validateStayNotStarted } from '../../util/prearrivalValidation';
import { ErrorDetail } from '../Error';
import { getResConfirmationDatePrefix } from '../../util/dataLayer/resConfirmationNumberUtil';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const DiningDetail: FC<DiningProps> = (props: any) => {
  const dineCategoryConst = 'dining_page';
  const { formData, setFormData, form_prearrival_questions_complete, setTotalPoint } = useCreateSurveyStore();

  const { isShowError: stayValidationError, errorMsg: stayErrorMsg } = validateStayNotStarted({
    arrivalDate: formData?.reservation?.arrival,
    isAuthorMode: props?.isAuthorMode ?? false,
    confirmationNumber: formData?.confirmationNumber || null,
    marshaCode: formData?.marshaCode || null,
  });

  const [checkedItems, setCheckedItems] = useState<{ [key: string]: boolean }>({});
  const [checkedDietryItems, setCheckedDietryItems] = useState<{ [key: string]: boolean }>({});

  const [diningOptionsArray, setDiningOptionsArray] = useState([]);
  const [propertyName, setPropertyName] = useState('');

  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const i18nText: any = props?.i18nMap;

  useEffect(() => {
    setTotalPoint();
    setPropertyName(formData?.property_config?.hotel_name);
  }, [formData?.property_config?.hotel_name]);

  const title = props.model?.mainHeading;
  const setDescription = props.model?.description;
  const diningExpText = props.model?.diningExperiencesText;
  const dietryPreference = props.model?.dietaryPreferencesHeading;
  const subTitle = props.model?.brandText;
  const brandText = subTitle.replace('%brand_name%', `<b>${propertyName}</b>`);
  const arrivalDate = dayjs(formData?.reservation?.arrival).format('dddd MM/DD/YYYY');
  const departureDate = dayjs(formData?.reservation?.departure).format('dddd MM/DD/YYYY');
  const [marshaCode, setMarshaCode] = useState('');
  const [alternateCID, setAlternateCID] = useState('');
  const [amenitiesData, setAmenitiesData] = useState([]);
  const [confirmationNumber, setConfirmationNumber] = useState('');
  const [currentDate, setCurrentDate] = useState(moment().format('MMDDYYYY'));
  const [otherDiningDetails, setOtherDiningDetails] = useState([]);

  useEffect(() => {
    setDiningOptionsArray(formData?.property_config?.other_dining_options || []);
    setAmenitiesData(formData?.property_config?.amenities ?? props.property_config?.amenities);
    setMarshaCode(formData?.property_config?.marsha_cd);
    setAlternateCID(formData?.reservation?.AlternateCustomerId);
    setConfirmationNumber(props?.reservation?.confirmationNumber || formData?.reservation?.confirmationNumber || '');
    setCurrentDate(getResConfirmationDatePrefix(props?.reservation?.confoCreate || formData?.reservation?.confoCreate));
    setOtherDiningDetails(formData.other_dining_details);
  }, [
    formData.property_config?.other_dining_options,
    formData.property_config?.amenities,
    formData.property_config?.marsha_cd,
    formData.reservation?.AlternateCustomerId,
    formData.reservation?.confirmationNumber,
    formData?.other_dining_details,
    formData.reservation?.confoCreate,
    props?.property_config?.amenities,
    props?.reservation?.confoCreate,
  ]);

  const combinedValue = `${currentDate}${confirmationNumber}`;

  const pathName = usePathname();
  const router = useRouter();
  const handleNextPage = (newPart?: string) => {
    setTotalPoint();
    updateDatalayer('form_prearrival_questions_complete', form_prearrival_questions_complete);
    updateDatalayer('form_step_name', 'Experience');
    const currentPath = pathName;
    const newPath = currentPath.replace(/\/[^/]*$/, `/${newPart}`);

    router.push(newPath);
  };
  /**
   * Determines whether compareDate satisfies the date range defined by startDate / endDate.
   *
   * Event mode rules (isEvent = true):
   *   • Both dates empty            → item is a Featured item; passes all event checks (returns true).
   *   • Only event_end_date present → event already started; check arrival ≤ event_end only.
   *                                   Departure has no lower bound to enforce → true.
   *   • Only event_start_date present → event is open-ended (no end); check departure ≥ event_start only.
   *                                     Arrival has no upper bound to enforce → true.
   *   • Both dates present          → full overlap check:
   *                                     arrival ≤ event_end  AND  departure ≥ event_start.
   *
   * Non-event mode (isEvent = false):
   *   • Checks today falls within [valid_start, valid_end].
   *   • A missing bound is treated as open-ended on that side.
   */
  const isDateInRange = (
    startDate: string | undefined,
    endDate: string | undefined,
    compareDate: dayjs.Dayjs,
    isEvent: boolean = false,
    isArrival: boolean = false
  ): boolean => {
    const startEmpty = !startDate || startDate.trim() === '';
    const endEmpty = !endDate || endDate.trim() === '';

    // Both bounds missing — featured item or always-valid schedule; no date constraint to apply.
    if (startEmpty && endEmpty) return true;

    const start = dayjs(startDate);
    const end = dayjs(endDate);

    if (isEvent) {
      if (isArrival) {
        // Rule: arrival <= event_end
        // No event_end → event is open-ended or already started with no defined end; arrival is always valid.
        if (endEmpty || !end.isValid()) return true;
        return compareDate.isSame(end, 'day') || compareDate.isBefore(end, 'day');
      } else {
        // Rule: departure >= event_start
        // No event_start → event already started before any known date; departure is always valid.
        if (startEmpty || !start.isValid()) return true;
        return compareDate.isSame(start, 'day') || compareDate.isAfter(start, 'day');
      }
    } else {
      // Schedule validity: today must fall within [valid_start, valid_end].
      // A missing bound is open-ended on that side.
      const afterStart =
        startEmpty || !start.isValid() || compareDate.isSame(start, 'day') || compareDate.isAfter(start, 'day');
      const beforeEnd =
        endEmpty || !end.isValid() || compareDate.isSame(end, 'day') || compareDate.isBefore(end, 'day');
      return afterStart && beforeEnd;
    }
  };

  const arrivalDate1 = dayjs(formData?.reservation?.arrival);
  const departureDate2 = dayjs(formData?.reservation?.departure);

  const filteredAmenities = amenitiesData?.filter((item: amenitiesDetailsProps) => {
    const isValidCategory = item.category === dineCategoryConst;

    const eventStartDate = item.event_start_date || '';
    const eventEndDate = item.event_end_date || '';
    const validStartDate = item.valid_start_date || '';
    const validEndEndDate = item.valid_end_date || '';

    // Checking if the current date falls within the valid schedule range
    const isValidSchedule = isDateInRange(validStartDate, validEndEndDate, dayjs());

    // Arrival must be on or before event_end. If event_end is empty, event is open-ended → always passes.
    const isValidArrivalDate = isDateInRange(eventStartDate, eventEndDate, arrivalDate1, true, true);

    // Departure must be on or after event_start. If event_start is empty, event already started → always passes.
    const isValidDepartureDate = isDateInRange(eventStartDate, eventEndDate, departureDate2, true, false);

    return isValidCategory && isValidArrivalDate && isValidDepartureDate && isValidSchedule;
  });

  const eventItems = filteredAmenities?.filter(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (data: any) => dayjs(data.event_start_date).isValid() || dayjs(data.event_end_date).isValid()
  );
  const featuredItems = filteredAmenities?.filter(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (data: any) => !dayjs(data.event_start_date).isValid() && !dayjs(data.event_end_date).isValid()
  );

  const labels = {
    mainHeading: title,
    subTitle1: brandText,
    subTitle2: setDescription,
    diningExperinceTitle:
      filteredAmenities?.length > 0
        ? props.model?.diningExperiencesHeading
        : props.model?.diningExperiencesHeading.replace('Other ', ''),
    dietryPreferenceTitle: dietryPreference,
    diningExpDetails: diningExpText,
  };

  // Check if any checkbox is checked
  const isAnyCheckboxChecked = Object.values(checkedDietryItems).some(value => value);
  const isAnyDiningCheckboxChecked = Object.values(checkedItems).some(value => value);
  const [colorSchemeforPage, setColorSchemeforPage] = useState('');
  const [backgroundImageUrl, setBackgroundImageUrl] = useState('');
  const [brandCodeProps, setBrandCodeProps] = useState('');

  useEffect(() => {
    if (props?.brandCode) {
      if (props?.property_config?.non_branded_flg === 'Y') {
        setColorSchemeforPage(brandColorSchemeGenerator('NBF'));
        setBrandCodeProps('NBF');
      } else if (props?.brandName === 'Ritz-Carlton Reserve') {
        setColorSchemeforPage(brandColorSchemeGenerator('RZR'));
        setBrandCodeProps('RZR');
      } else {
        setColorSchemeforPage(brandColorSchemeGenerator(props?.brandCode));
        setBrandCodeProps(props?.brandCode);
      }
    } else {
      if (formData?.property_config?.non_branded_flg === 'Y') {
        setColorSchemeforPage(brandColorSchemeGenerator('NBF'));
        setBrandCodeProps('NBF');
      } else {
        setColorSchemeforPage(brandColorSchemeGenerator(formData?.property_config?.brand_cd || formData?.brandCode));
        setBrandCodeProps(formData?.property_config?.brand_cd || formData?.brandCode);
      }
    }
    if (props?.backgroundImage) {
      setBackgroundImageUrl(props?.backgroundImage);
    } else {
      setBackgroundImageUrl(formData?.property_config?.background_image_url);
    }
  }, [
    formData?.property_config?.background_image_url,
    formData?.property_config?.brand_cd,
    props?.backgroundImage,
    props?.brandCode,
    props?.brandName,
  ]);

  // Handle checkbox change
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDinigCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, checked, value } = e.target;
    setCheckedItems(prevState => ({
      ...prevState,
      [name]: checked,
    }));

    if (checked) {
      setFormData({ ...formData, other_dining_details: [...formData.other_dining_details, removeXSSScripts(value)] });
    } else {
      if (formData.other_dining_details?.length === 1) {
        setFormData({
          ...formData,
          other_dining_details: formData.other_dining_details.filter((str: string) => str !== value),
          dining_notes: '',
        });
      } else {
        setFormData({
          ...formData,
          other_dining_details: formData.other_dining_details.filter((str: string) => str !== value),
        });
      }
    }
  };

  //Handle Dietry checkbox
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDietryCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, checked, value } = e.target;
    setCheckedDietryItems(prevState => ({
      ...prevState,
      [name]: checked,
    }));

    checked
      ? setFormData({
          ...formData,
          dietary_restrictions_details: [...formData.dietary_restrictions_details, removeXSSScripts(value)],
        })
      : setFormData({
          ...formData,
          dietary_restrictions_details: formData.dietary_restrictions_details.filter((str: string) => str !== value),
        });

    if (checked) {
      setFormData({
        ...formData,
        dietary_restrictions_details: [...formData.dietary_restrictions_details, removeXSSScripts(value)],
      });
    } else {
      if (formData.dietary_restrictions_details?.length === 1) {
        setFormData({
          ...formData,
          dietary_restrictions_details: formData.dietary_restrictions_details.filter((str: string) => str !== value),
          dining_dietary_restrictions_notes: '',
        });
      } else {
        setFormData({
          ...formData,
          dietary_restrictions_details: formData.dietary_restrictions_details.filter((str: string) => str !== value),
        });
      }
    }
  };

  const diningOptionsData: Array<{ label: string; name: string; enabled: string }> =
    diningOptionsArray.length > 0 ? diningOptionsArray : diningCheckboxData;

  const midIndex = Math.ceil(diningOptionsData?.length / 2);
  const firstHalf = diningOptionsData.slice(0, midIndex);
  const secondHalf = diningOptionsData.slice(midIndex);

  const midIndex1 = Math.ceil(dietryCheckboxData?.length / 2);
  const firstHalf1 = dietryCheckboxData.slice(0, midIndex1 + 1);
  const secondHalf1 = dietryCheckboxData.slice(midIndex1 + 1);
  type DivID = 'div1';

  const [dietryRestriction, setDietryRestriction] = useState('');

  useEffect(() => {
    setDietryRestriction(formData.dietary_restrictions);
  }, [formData.dietary_restrictions]);

  const [isVisible, setIsVisible] = useState<{ [key in DivID]: boolean }>({
    div1: dietryRestriction === 'Yes',
  });

  useEffect(() => {
    setIsVisible(prevState => ({
      ...prevState,
      div1: dietryRestriction === 'Yes',
    }));
  }, [dietryRestriction]);

  const toggleVisibility = (id: DivID) => {
    setIsVisible({
      ...isVisible,
      [id]: !isVisible[id], // Toggle visibility for the div with the given id
    });
    if (id === 'div1') {
      const newVisibility = !isVisible[id];
      if (!newVisibility) {
        setFormData({
          ...formData,
          dietary_restrictions_details: [],
          dietary_restrictions: 'No',
          dining_dietary_restrictions_notes: '',
        });
      } else {
        setFormData({
          ...formData,
          dietary_restrictions: 'Yes',
        });
      }
    }
  };

  const inputId = useId();
  // const checkBoxId = useId();

  const sanitizedNode1 = DOMPurify.sanitize(labels.subTitle1, { RETURN_DOM: true }) as HTMLElement;
  const fragmentString1 = sanitizedNode1.innerHTML;

  const sanitizedNode2 = DOMPurify.sanitize(labels.subTitle2, { RETURN_DOM: true }) as HTMLElement;
  const fragmentString2 = sanitizedNode2.innerHTML;

  const sanitizedNode3 = DOMPurify.sanitize(labels.diningExpDetails, { RETURN_DOM: true }) as HTMLElement;
  const fragmentString3 = sanitizedNode3.innerHTML;

  return (
    <EditableComponent {...props}>
      {stayValidationError ? (
        <SurveyWrapper
          marshaCode={marshaCode}
          backgroundImage={backgroundImageUrl}
          className="brand-css common-color-set"
          colorScheme={colorSchemeforPage}
          alternateId={alternateCID}
          productInfo={marshaCode}
          formPageName="Dining"
          formName="Prearrival Travel Form"
          env_api_tile_error_info=""
          prearrival_form_amenities_added_to_plan={formData.prearrivalformamenitiesaddedtoplan}
          prearrival_form_booking_confirmation_id={combinedValue}
          form_prearrival_questions_complete={form_prearrival_questions_complete}
          beforeSuveryWrapperOuter={props?.model?.beforeSuveryWrapperOuter}
          afterSuveryWrapperOuter={props?.model?.afterSuveryWrapperOuter}
        >
          <ErrorDetail
            hotelEmail={props?.hotelEmail}
            hotelPhoneNumber={props?.hotelPhoneNumber}
            confirmationNumber={formData?.reservation?.confirmationNumber}
            errorVal={stayErrorMsg}
          />
        </SurveyWrapper>
      ) : (
        <SurveyWrapper
          marshaCode={marshaCode}
          backgroundImage={backgroundImageUrl}
          className="brand-css common-color-set"
          colorScheme={colorSchemeforPage}
          alternateId={alternateCID}
          productInfo={marshaCode}
          formPageName="Dining"
          formName="Prearrival Travel Form"
          env_api_tile_error_info=""
          prearrival_form_amenities_added_to_plan={formData.prearrivalformamenitiesaddedtoplan}
          prearrival_form_booking_confirmation_id={combinedValue}
          form_prearrival_questions_complete={form_prearrival_questions_complete}
          beforeSuveryWrapperOuter={props?.model?.beforeSuveryWrapperOuter}
          afterSuveryWrapperOuter={props?.model?.afterSuveryWrapperOuter}
        >
          <div className="aem-GridColumn aem-GridColumn--default--12" data-testid="Dining Description">
            <StyledDiningDetail data-testid="dining-detail" data-component-name="o-account-diningDetail">
              <SurveyWrapperInner
                beforeSuveryWrapperInner={props?.model?.beforeSuveryWrapperInner}
                afterSuveryWrapperInner={props?.model?.afterSuveryWrapperInner}
              >
                <div className="dining-section">
                  <div className="container">
                    <div className="row">
                      <div className="col-12 pr-0 px-0">
                        <div className="lr-no-padding container col-12 col-xl-10">
                          <Heading
                            variation={Types.headingType.title}
                            fontSize={Types.size.large}
                            element={Types.tags.h1}
                            titleText={labels.mainHeading}
                          />
                          <div>
                            <Text
                              element={Types.tags.div}
                              fontSize={Types.size.small}
                              dangerouslySetInnerHTML={{ __html: fragmentString1 }}
                              customClass="subtext mb-2"
                            ></Text>
                            <Text
                              element={Types.tags.div}
                              fontSize={Types.size.medium}
                              dangerouslySetInnerHTML={{ __html: fragmentString2 }}
                              customClass="noteText"
                            ></Text>
                          </div>
                          {eventItems && eventItems?.length > 0 && (
                            <Heading
                              variation={Types.headingType.title}
                              fontSize={Types.size.large}
                              element={Types.tags.h3}
                              titleText={props?.i18nMap?.upcomingDiningEvents || 'Upcoming Dining Events'}
                              customClass="small-h6 mb-0 heading-top-margin"
                            />
                          )}
                          {eventItems && eventItems?.length > 0 && (
                            <CarouselContainer
                              amenitiesDetails={eventItems}
                              componentType="Dining"
                              arrivalDate={arrivalDate}
                              departureDate={departureDate}
                              eventId="Upcoming"
                              componentEventId="Dining_Upcoming"
                              brandCode={brandCodeProps}
                            />
                          )}
                          {featuredItems && featuredItems?.length > 0 && (
                            <Heading
                              variation={Types.headingType.title}
                              fontSize={Types.size.large}
                              element={Types.tags.h3}
                              titleText={props?.i18nMap?.featuredDiningExperiences || 'Featured Dining Experiences'}
                              customClass="small-h6 mb-0 heading-top-margin"
                            />
                          )}
                          {featuredItems?.length > 0 && (
                            <CarouselContainer
                              amenitiesDetails={featuredItems}
                              componentType="Dining"
                              arrivalDate={arrivalDate}
                              departureDate={departureDate}
                              eventId="Featured_Event"
                              componentEventId="Dining_Featured_Event"
                              brandCode={brandCodeProps}
                            />
                          )}
                          {featuredItems?.length === 0 && <div className="mt-5"></div>}
                          <div>
                            {featuredItems?.length > 0 && <hr />}

                            <Heading
                              variation={Types.headingType.title}
                              fontSize={Types.size.extraSmall}
                              element={Types.tags.h3}
                              titleText={labels.diningExperinceTitle}
                              customClass="small-h6 mb-3"
                            />
                            <Text
                              element={Types.tags.paragraph}
                              fontSize={Types.size.small}
                              dangerouslySetInnerHTML={{ __html: fragmentString3 }}
                              customClass="subTitle"
                            ></Text>
                            <div className=" col-12 col-xl-10 px-0 isActive">
                              <div className="d-flex justify-content-between align-items-center flex-row flex-row-desktop mt-4 mb-3">
                                <div className="row diningref">
                                  <div className="col-md-6 first-col">
                                    {firstHalf.map((checkbox, index) => (
                                      <CheckBox
                                        key={`checkbox-${index}`}
                                        checkboxName={checkbox.name}
                                        checkboxLabel={checkbox.label}
                                        checked={
                                          formData &&
                                          formData.other_dining_details &&
                                          formData.other_dining_details.includes(checkbox.label)
                                        }
                                        onChange={handleDinigCheckboxChange}
                                        checkboxId={`checkbox-${index}`}
                                        data-testid={`checkbox-${checkbox.name}`}
                                        value={checkbox.label}
                                      />
                                    ))}
                                  </div>
                                  <div className="col-md-6 second-col">
                                    {secondHalf.map((checkbox, index) => (
                                      <CheckBox
                                        key={`checkbox-${midIndex + index}`}
                                        checkboxName={checkbox.name}
                                        checkboxLabel={checkbox.label}
                                        checked={
                                          formData &&
                                          formData.other_dining_details &&
                                          formData.other_dining_details.includes(checkbox.label)
                                        }
                                        onChange={handleDinigCheckboxChange}
                                        checkboxId={`checkbox-${midIndex + index}`}
                                        data-testid={`checkbox-${checkbox.name}`}
                                        value={checkbox.label}
                                      />
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                            {(isAnyDiningCheckboxChecked || otherDiningDetails?.length > 0) && (
                              <TextArea
                                id={`${inputId}-dining_notes`}
                                key="dining_notes"
                                name="dining_notes"
                                label="dining_notes"
                                placeHolderText={props?.i18nMap?.pleaseAddNotes || 'Please add notes here'}
                                getInputValue={(val: string) => {
                                  setFormData({ ...formData, dining_notes: removeXSSScripts(val) });
                                }}
                                inputValue={removeXSSScripts(formData?.dining_notes)}
                                maxLength={255}
                                messageError={
                                  props?.i18nMap?.limitInput255 || 'Please limit your input to 255 characters'
                                }
                              ></TextArea>
                            )}
                          </div>
                          <div>
                            <hr />
                            <Heading
                              variation={Types.headingType.title}
                              fontSize={Types.size.extraSmall}
                              element={Types.tags.h3}
                              titleText={labels.dietryPreferenceTitle}
                              customClass="small-h6 mb-3"
                            />
                            <div
                              className={`d-flex align-items-center flex-row switch-toggle ${
                                isVisible['div1'] && 'mb-2'
                              }`}
                            >
                              <span className="toggle-text-no">{props?.i18nMap?.no || 'No'}</span>
                              <Switch
                                toggleId="toggleswitch22"
                                toggleName="toggle"
                                onChange={() => toggleVisibility('div1')}
                                selected={formData.dietary_restrictions === 'Yes'}
                              >
                                <span className="sr-only">Dietary Preferences / Restrictions?</span>
                              </Switch>
                              <span className="toggle-text">{props?.i18nMap?.yes || 'Yes'}</span>
                            </div>
                            {isVisible['div1'] && (
                              <div className=" col-12 col-xl-10 px-0 isActive">
                                <div className="d-flex justify-content-between align-items-center flex-row flex-row-desktop mb-3">
                                  <div className="row dietry-pref">
                                    <div className="col-md-6 first-col">
                                      {firstHalf1.map((dietbox, index) => (
                                        <CheckBox
                                          key={`dietbox-${index}-${dietbox.name}`}
                                          checkboxName={dietbox.name}
                                          checkboxLabel={dietbox.label}
                                          checked={
                                            formData &&
                                            formData.dietary_restrictions_details &&
                                            formData.dietary_restrictions_details.includes(dietbox.label)
                                          }
                                          onChange={handleDietryCheckboxChange}
                                          checkboxId={`dietbox-${index}`}
                                          data-testid={`dietbox-${dietbox.name}`}
                                          value={dietbox.label}
                                        />
                                      ))}
                                    </div>
                                    <div className="col-md-6 second-col">
                                      {secondHalf1.map((dietbox, index) => (
                                        <CheckBox
                                          key={`dietbox-${midIndex1 + 1 + index}`}
                                          checkboxName={dietbox.name}
                                          checkboxLabel={dietbox.label}
                                          checked={
                                            formData &&
                                            formData.dietary_restrictions_details &&
                                            formData.dietary_restrictions_details.includes(dietbox.label)
                                          }
                                          onChange={handleDietryCheckboxChange}
                                          checkboxId={`dietbox-${midIndex1 + 1 + index}`}
                                          data-testid={`dietbox-${dietbox.name}`}
                                          value={dietbox.label}
                                        />
                                      ))}
                                      <CheckBox
                                        data-testid="checkbox-others"
                                        key="checkbox-others"
                                        checkboxName="other"
                                        checkboxLabel="Other"
                                        checked={
                                          formData &&
                                          formData.dietary_restrictions_details &&
                                          formData.dietary_restrictions_details.includes('Other')
                                        }
                                        onChange={handleDietryCheckboxChange}
                                        checkboxId="checkbox-others"
                                        value="Other"
                                      />
                                    </div>
                                  </div>
                                </div>
                                {(isAnyCheckboxChecked || formData.dietary_restrictions_details?.length > 0) && (
                                  <TextArea
                                    id={`${inputId}-dining_dietary_restrictions_notes`}
                                    key="dining_dietary_restrictions_notes"
                                    name="dining_dietary_restrictions_notes"
                                    label="dining_dietary_restrictions_notes"
                                    placeHolderText={props?.i18nMap?.pleaseAddNotes || 'Please add notes here'}
                                    getInputValue={(val: string) => {
                                      setFormData({
                                        ...formData,
                                        dining_dietary_restrictions_notes: removeXSSScripts(val),
                                      });
                                    }}
                                    inputValue={removeXSSScripts(formData.dining_dietary_restrictions_notes)}
                                    maxLength={255}
                                    messageError={
                                      props?.i18nMap?.limitInput255 || 'Please limit your input to 255 characters'
                                    }
                                  ></TextArea>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="align-items-end justify-content-end flex-row d-flex d-flex-mobile col-12 p-0 mt-4">
                            <Button
                              className="m-button-m m-button-secondary custom-btn mx-0 "
                              type={Types.ButtonTypeVariation.Submit}
                              buttonCopy="Previous"
                              callback={() => handleNextPage('guest-details.mi')}
                            ></Button>
                            <Button
                              className="m-button-m m-button-primary custom-btn mx-0 "
                              type={Types.ButtonTypeVariation.Submit}
                              buttonCopy={props?.i18nMap?.continue || 'Continue'}
                              callback={() => handleNextPage('experiences.mi')}
                            ></Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </SurveyWrapperInner>
            </StyledDiningDetail>
          </div>
        </SurveyWrapper>
      )}
    </EditableComponent>
  );
};
